import { Table, Button, Modal, Form, Input, Select, Space, Card, Tabs, message, Spin, Checkbox, Tag, Switch, Tooltip } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAllRegionStores } from '../hooks/useStores';
import { listUsersAPI, createUserAPI, updateUserAPI, deleteUserAPI, changePasswordAPI } from '../api/auth.api';
import api from '../api/client';

interface ReconciliationRow {
  storeId: number;
  storeName: string;
  ssCount: number | null;
  localCount: number;
  delta: number | null;
  checkedAt: number | null;
  error?: string;
}

// 1hr TTL matches backend RECON_TTL_MS.
const RECON_STALE_MS = 60 * 60_000;

export interface User {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  name: string;
  type: 'user' | 'admin' | 'superadmin';
  permissions: string[];
  // Backend returns assignedStores as number[]; some callers still show a name join client-side.
  assignedStores: Array<number | { storeId: number | string; name?: string }>;
  region: 'CA' | 'US' | 'BOTH';
  createdAt?: string;
  updatedAt?: string;
}

const PERMISSION_OPTIONS = [
  { label: 'Orders', options: [{ label: 'View Orders', value: 'order_view' }] },
  { label: 'Shipments', options: [{ label: 'View Shipments', value: 'shipment_view' }] },
  { label: 'Return', options: [{ label: 'View Return', value: 'return_view' }] },
  { label: 'Export', options: [{ label: 'View Export', value: 'export_view' }] },
  { label: 'Products', options: [{ label: 'View Products', value: 'product_view' }] },
  { label: 'Inventory', options: [{ label: 'View Inventory', value: 'inventory_view' }] },
];

const PERMISSION_LABELS: Record<string, string> = {
  order_view: 'View Orders',
  shipment_view: 'View Shipments',
  return_view: 'View Return',
  export_view: 'View Export',
  product_view: 'View Products',
  inventory_view: 'View Inventory',
};

// All assignable permissions for the matrix
const ALL_PERMISSIONS = [
  { value: 'order_view', label: 'View Orders' },
  { value: 'shipment_view', label: 'View Shipments' },
  { value: 'return_view', label: 'View Return' },
  { value: 'export_view', label: 'View Export' },
  { value: 'product_view', label: 'View Products' },
  { value: 'inventory_view', label: 'View Inventory' },
  { value: 'reports:read', label: 'Access Reports' },
];

// Role overview data — what each user type can do
const ROLE_OVERVIEW = [
  {
    key: 'superadmin',
    role: 'Super Admin',
    roleTag: <Tag color="purple">Super Admin</Tag>,
    viewOrders: true,
    viewShipments: true,
    manageUsers: true,
    editDeleteUsers: true,
  },
  {
    key: 'admin',
    role: 'Admin',
    roleTag: <Tag color="red">Admin</Tag>,
    viewOrders: true,
    viewShipments: true,
    manageUsers: true,
    editDeleteUsers: false,
  },
  {
    key: 'user',
    role: 'User',
    roleTag: <Tag color="blue">User</Tag>,
    viewOrders: null,
    viewShipments: null,
    manageUsers: false,
    editDeleteUsers: false,
  },
];

const Yes = () => <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />;
const No = () => <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 18 }} />;
const Custom = () => <Tag color="geekblue">Custom</Tag>;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, isSuperAdmin ,isAdmin} = useAuth();
  const stores = useAllRegionStores();
  const [reconRows, setReconRows] = useState<ReconciliationRow[] | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [checkingStoreId, setCheckingStoreId] = useState<number | null>(null);
  const [fixingStoreId, setFixingStoreId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [allowEmailEdit, setAllowEmailEdit] = useState(false);
  const [permSaving, setPermSaving] = useState<Record<string, boolean>>({});
  const watchedType = Form.useWatch('type', form);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      loadUsers();
    }
  }, [isAuthenticated, isLoading, navigate]);

  const loadUsers = async () => {
    setTableLoading(true);
    try {
      const data = await listUsersAPI();
      setUsers(data);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to load users');
    } finally {
      setTableLoading(false);
    }
  };

  const handleCreateOrUpdateUser = async (values: any) => {
    setLoading(true);
    try {
      const permissions = values.type === 'admin' || values.type === 'superadmin' ? [] : (values.permissions ?? []);
      const assignedStores = (values.assignedStores ?? []).map((s: string | number) => Number(s));
      if (editingUserId) {
        await updateUserAPI(Number(editingUserId), {
          email: values.email,
          name: values.name,
          region: values.region ?? 'CA',
          assignedStores,
          type: values.type,
          permissions,
        });
        if (showPasswordFields && values.newPassword) {
          await changePasswordAPI(Number(editingUserId), values.newPassword);
        }
        message.success('User updated successfully');
        setEditingUserId(null);
        setShowPasswordFields(false);
      } else {
        await createUserAPI({
          email: values.email,
          name: values.name,
          password: values.password,
          region: values.region ?? 'CA',
          assignedStores,
          type: values.type,
          permissions,
        });
        message.success('User created successfully');
      }
      form.resetFields();
      setIsModalVisible(false);
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    Modal.confirm({
      title: 'Delete User',
      content: 'Are you sure you want to delete this user?',
      okText: 'Yes',
      cancelText: 'No',
      async onOk() {
        try {
          await deleteUserAPI(Number(id));
          message.success('User deleted successfully');
          loadUsers();
        } catch (error: any) {
          message.error(error.response?.data?.error || 'Failed to delete user');
        }
      }
    });
  };

  const handleEditUser = (u: User) => {
    setEditingUserId(u.id);
    setShowPasswordFields(false);
    form.setFieldsValue({
      email: u.email,
      name: u.name,
      type: u.type || 'user',
      region: u.region ?? 'CA',
      permissions: u.permissions ?? [],
      assignedStores: u.assignedStores.map((s) => (typeof s === 'object' ? String(s.storeId) : String(s))),
    });
    setIsModalVisible(true);
  };

  const handleTogglePermission = async (record: User, permission: string, checked: boolean) => {
    const key = `${record.id}-${permission}`;
    setPermSaving(prev => ({ ...prev, [key]: true }));
    try {
      const newPerms = checked
        ? [...(record.permissions ?? []), permission]
        : (record.permissions ?? []).filter(p => p !== permission);
      const assignedStores = record.assignedStores.map((s) => (typeof s === 'object' ? Number(s.storeId) : Number(s)));
      await updateUserAPI(record.id, {
        email: record.email,
        name: record.name,
        region: record.region,
        assignedStores,
        type: record.type,
        permissions: newPerms,
      });
      setUsers(prev => prev.map(u => u.id === record.id ? { ...u, permissions: newPerms } : u));
      message.success('Permission updated');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update permission');
    } finally {
      setPermSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const loadReconSnapshot = async () => {
    setReconLoading(true);
    try {
      const res = await api.get<{ rows: ReconciliationRow[] }>('/stores/reconciliation');
      setReconRows(res.data.rows);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load reconciliation');
    } finally {
      setReconLoading(false);
    }
  };

  const reconcileOneStore = async (storeId: number, force: boolean): Promise<ReconciliationRow | null> => {
    setCheckingStoreId(storeId);
    try {
      const res = await api.get<ReconciliationRow>(
        `/stores/${storeId}/reconciliation${force ? '?force=true' : ''}`,
        { timeout: 60_000 },
      );
      setReconRows((prev) =>
        prev ? prev.map((r) => (r.storeId === storeId ? res.data : r)) : prev,
      );
      return res.data;
    } catch (err: any) {
      message.error(`Store ${storeId}: ${err.response?.data?.error || 'recon failed'}`);
      return null;
    } finally {
      setCheckingStoreId(null);
    }
  };

  // Auto-run: iterate stores sequentially. Skip any store whose cached
  // checkedAt is within RECON_STALE_MS. Continues from wherever it left off
  // if interrupted (stale rows remain stale until picked up on next run).
  const autoReconcile = async () => {
    if (!reconRows) return;
    setAutoRunning(true);
    try {
      const now = Date.now();
      for (const row of reconRows) {
        const fresh = row.checkedAt != null && now - row.checkedAt < RECON_STALE_MS;
        if (fresh) continue;
        await reconcileOneStore(row.storeId, false);
      }
      message.success('Auto reconciliation complete');
    } finally {
      setAutoRunning(false);
    }
  };

  const fixStoreDrift = async (storeId: number, storeName: string) => {
    setFixingStoreId(storeId);
    try {
      const res = await api.post<{
        ssAwaitingSeen: number;
        upsertedFromSs: number;
        staleLocalIds: number;
        staleRefreshed: number;
        staleFailed: number;
        errors: string[];
      }>(`/stores/${storeId}/fix-drift`, {}, { timeout: 180_000 });
      const d = res.data;
      message.success(
        `${storeName}: pulled ${d.upsertedFromSs} from SS, refreshed ${d.staleRefreshed}/${d.staleLocalIds} stale local`,
      );
      if (d.staleFailed > 0) message.warning(`${d.staleFailed} refresh failures — check logs`);
      // Re-run this store's recon (backend cleared its cache on fix).
      await reconcileOneStore(storeId, true);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Fix drift failed');
    } finally {
      setFixingStoreId(null);
    }
  };

  const userColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        if (type === 'superadmin') return <Tag color="purple">Super Admin</Tag>;
        if (type === 'admin') return <Tag color="red">Admin</Tag>;
        return <Tag color="blue">User</Tag>;
      }
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (region: 'CA' | 'US' | 'BOTH' | undefined) => {
        if (region === 'BOTH') return <Tag color="geekblue">CA + US</Tag>;
        if (region === 'US') return <Tag color="cyan">US</Tag>;
        return <Tag color="magenta">CA</Tag>;
      },
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[], record: User) => {
        if (record.type === 'superadmin') return <Tag color="gold">Full Access</Tag>;
        if (record.type === 'admin') return <Tag color="orange">View Only</Tag>;
        if (!permissions || permissions.length === 0) return <span style={{ color: '#999' }}>None</span>;
        return (
          <Space wrap>
            {permissions.map(p => (
              <Tag key={p} color="green">{PERMISSION_LABELS[p] ?? p}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Assigned Stores',
      dataIndex: 'assignedStores',
      key: 'assignedStores',
      // Semantic: empty array == access to ALL stores in the user's
      // region(s). The backend / order-list filter treats missing storeIds
      // as no restriction, so this matches actual behavior. Newly-added
      // stores are automatically included for these users — no need to
      // re-assign them each time SS adds a store.
      render: (assignedStores: Array<number | { storeId: number | string; name?: string }>) => {
        if (!assignedStores || assignedStores.length === 0) {
          return <Tag color="green">All stores</Tag>;
        }
        const nameFor = (id: number | string): string => {
          const found = stores.find((st) => String(st.storeId) === String(id));
          return found?.storeName ?? `#${id}`;
        };
        const names = assignedStores.map((s) =>
          typeof s === 'object' ? (s.name ?? nameFor(s.storeId)) : nameFor(s),
        );
        return names.join(', ');
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('en-US')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        isSuperAdmin ? (
          <Space>
            <Button type="primary" size="small" onClick={() => handleEditUser(record)}>Edit</Button>
            <Button danger size="small" onClick={() => handleDeleteUser(record.id)}>Delete</Button>
          </Space>
        ) : <span style={{ color: '#999' }}>No Access</span>
      )
    }
  ];

  // Columns for permission matrix (type=user only)
  const permissionMatrixColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    ...ALL_PERMISSIONS.map(perm => ({
      title: (
        <Tooltip title={perm.label}>
          <span>{perm.label}</span>
        </Tooltip>
      ),
      key: perm.value,
      align: 'center' as const,
      render: (_: any, record: User) => (
        <Switch
          checked={(record.permissions ?? []).includes(perm.value)}
          loading={!!permSaving[`${record.id}-${perm.value}`]}
          disabled={!isSuperAdmin}
          onChange={checked => handleTogglePermission(record, perm.value, checked)}
          size="small"
        />
      )
    }))
  ];

  const roleOverviewColumns = [
    { title: 'Role', key: 'role', render: (_: any, r: any) => r.roleTag },
    {
      title: 'View Orders',
      key: 'viewOrders',
      align: 'center' as const,
      render: (_: any, r: any) => r.viewOrders === null ? <Custom /> : r.viewOrders ? <Yes /> : <No />
    },
    {
      title: 'View Shipments',
      key: 'viewShipments',
      align: 'center' as const,
      render: (_: any, r: any) => r.viewShipments === null ? <Custom /> : r.viewShipments ? <Yes /> : <No />
    },
    {
      title: 'Manage Users (View)',
      key: 'manageUsers',
      align: 'center' as const,
      render: (_: any, r: any) => r.manageUsers ? <Yes /> : <No />
    },
    {
      title: 'Edit / Delete Users',
      key: 'editDeleteUsers',
      align: 'center' as const,
      render: (_: any, r: any) => r.editDeleteUsers ? <Yes /> : <No />
    },
  ];

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1>Admin Dashboard</h1>
      </div>

      <Tabs
        items={[
          {
            key: 'users',
            label: 'User Management',
            children: (
              <Card>
                {(isSuperAdmin || isAdmin )&& (
                  <div style={{ marginBottom: 20 }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setEditingUserId(null);
                        form.resetFields();
                        setIsModalVisible(true);
                      }}
                    >
                      Create New User
                    </Button>
                  </div>
                )}
                <Table
                  columns={userColumns}
                  dataSource={users}
                  rowKey="id"
                  loading={tableLoading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: 'No users created yet' }}
                />
              </Card>
            )
          },
         
          {
            key: 'stores',
            label: 'Stores',
            children: (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0 }}>Available Stores</h3>
                  <Space>
                    {!reconRows && (
                      <Button
                        icon={<ReloadOutlined />}
                        loading={reconLoading}
                        onClick={loadReconSnapshot}
                      >
                        Load Awaiting Comparison
                      </Button>
                    )}
                    {reconRows && (
                      <>
                        <Tooltip title="Reconcile each store in sequence. Skips any store checked in the last hour — pick up where you left off.">
                          <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            loading={autoRunning}
                            onClick={autoReconcile}
                          >
                            Auto Reconcile
                          </Button>
                        </Tooltip>
                        <Button onClick={loadReconSnapshot} loading={reconLoading}>
                          Reload Snapshot
                        </Button>
                      </>
                    )}
                  </Space>
                </div>
                <Table
                  columns={[
                    { title: 'Store Name', dataIndex: 'storeName', key: 'storeName' },
                    { title: 'Store ID', dataIndex: 'storeId', key: 'storeId', width: 120 },
                    ...(reconRows
                      ? [
                          {
                            title: 'Status',
                            key: 'reconStatus',
                            width: 130,
                            render: (_: unknown, r: { storeId: number | string }) => {
                              const row = reconRows.find((x) => x.storeId === Number(r.storeId));
                              if (!row) return '-';
                              if (checkingStoreId === Number(r.storeId)) {
                                return <Tag color="blue"><Spin size="small" /> checking</Tag>;
                              }
                              if (row.error) {
                                return <Tooltip title={row.error}><Tag color="red">error</Tag></Tooltip>;
                              }
                              if (row.checkedAt == null) return <Tag>never checked</Tag>;
                              const age = Date.now() - row.checkedAt;
                              const mins = Math.floor(age / 60_000);
                              if (age >= RECON_STALE_MS) return <Tag color="orange">stale ({mins}m)</Tag>;
                              return <Tag color="green">fresh ({mins}m)</Tag>;
                            },
                          },
                          {
                            title: 'SS Awaiting',
                            key: 'ssCount',
                            width: 110,
                            render: (_: unknown, r: { storeId: number | string }) => {
                              const row = reconRows.find((x) => x.storeId === Number(r.storeId));
                              return row?.ssCount ?? '-';
                            },
                          },
                          {
                            title: 'Local Awaiting',
                            key: 'localCount',
                            width: 120,
                            render: (_: unknown, r: { storeId: number | string }) => {
                              const row = reconRows.find((x) => x.storeId === Number(r.storeId));
                              return row ? row.localCount : '-';
                            },
                          },
                          {
                            title: 'Delta (Local - SS)',
                            key: 'delta',
                            width: 150,
                            render: (_: unknown, r: { storeId: number | string }) => {
                              const row = reconRows.find((x) => x.storeId === Number(r.storeId));
                              if (!row || row.delta == null) return '-';
                              if (row.delta === 0) return <Tag color="green">match</Tag>;
                              return <Tag color={row.delta > 0 ? 'orange' : 'red'}>{row.delta > 0 ? `+${row.delta}` : row.delta}</Tag>;
                            },
                          },
                          {
                            title: 'Actions',
                            key: 'actions',
                            width: 200,
                            render: (_: unknown, r: { storeId: number | string; storeName: string }) => {
                              const row = reconRows.find((x) => x.storeId === Number(r.storeId));
                              const sid = Number(r.storeId);
                              const isChecking = checkingStoreId === sid;
                              const isFixing = fixingStoreId === sid;
                              const showFix = row && row.delta != null && row.delta !== 0;
                              return (
                                <Space>
                                  <Tooltip title="Force re-check this store now (bypasses 1hr cache)">
                                    <Button
                                      size="small"
                                      loading={isChecking}
                                      disabled={autoRunning}
                                      onClick={() => reconcileOneStore(sid, true)}
                                    >
                                      Recheck
                                    </Button>
                                  </Tooltip>
                                  {showFix && (
                                    <Tooltip title="Pull all awaiting orders from SS for this store and refresh stale local rows">
                                      <Button
                                        size="small"
                                        type="primary"
                                        loading={isFixing}
                                        disabled={autoRunning}
                                        onClick={() => fixStoreDrift(sid, r.storeName)}
                                      >
                                        Fix drift
                                      </Button>
                                    </Tooltip>
                                  )}
                                </Space>
                              );
                            },
                          },
                        ]
                      : []),
                  ]}
                  dataSource={stores}
                  rowKey="storeId"
                  pagination={{
                    defaultPageSize: 25,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100],
                    hideOnSinglePage: true,
                    showTotal: (total) => `${total} stores`,
                  }}
                />
              </Card>
            )
          },
        ]}
      />

      <Modal
        title={editingUserId ? 'Edit User' : 'Create New User'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingUserId(null);
          setShowPasswordFields(false);
          setAllowEmailEdit(false);
        }}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrUpdateUser}>
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input type="email" placeholder="user@example.com" disabled={!!editingUserId && !allowEmailEdit} />
          </Form.Item>
          {editingUserId && !allowEmailEdit && (
            <div style={{ marginBottom: 16 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setAllowEmailEdit(true); }}>Edit Email</a>
            </div>
          )}
          {editingUserId && allowEmailEdit && (
            <div style={{ marginBottom: 16 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setAllowEmailEdit(false); }}>Cancel</a>
            </div>
          )}

          <Form.Item
            label="Full Name"
            name="name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            label="User Type"
            name="type"
            initialValue="user"
            rules={[{ required: true, message: 'Please select user type' }]}
          >
            <Select
              options={[
                { label: 'User', value: 'user' },
                { label: 'Admin', value: 'admin' },
                { label: 'Super Admin', value: 'superadmin' }
              ]}
            />
          </Form.Item>

          {watchedType !== 'admin' && watchedType !== 'superadmin' && (
            <Form.Item label="Permissions" name="permissions" initialValue={[]}>
              <Checkbox.Group style={{ width: '100%' }}>
                {PERMISSION_OPTIONS.map(group => (
                  <div key={group.label} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4, color: '#555' }}>{group.label}</div>
                    <div style={{ paddingLeft: 12 }}>
                      {group.options.map(opt => (
                        <Checkbox key={opt.value} value={opt.value} style={{ marginBottom: 4 }}>
                          {opt.label}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                ))}
              </Checkbox.Group>
            </Form.Item>
          )}

          {!editingUserId && (
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}

          <Form.Item
            label="Region"
            name="region"
            initialValue="CA"
            rules={[{ required: true, message: 'Select region access' }]}
            tooltip="Which regional platform(s) this user can access. BOTH gives them a region picker in the header."
          >
            <Select
              options={[
                { label: 'CA only', value: 'CA' },
                { label: 'US only', value: 'US' },
                { label: 'Both (CA + US)', value: 'BOTH' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Assign Stores{' '}
                <a
                  style={{ fontSize: 12, marginLeft: 8, color: '#999' }}
                  onClick={() => form.setFieldsValue({ assignedStores: [] })}
                >
                  Clear (= all stores)
                </a>
              </span>
            }
            name="assignedStores"
            tooltip="Leave empty to give this user access to ALL stores in their region(s). New stores added later are automatically included. Only add specific stores here if you want to LIMIT access."
          >
            <Select
              mode="multiple"
              placeholder="Leave empty for access to all stores"
              options={stores.map(store => ({
                label: store.storeName,
                value: String(store.storeId)
              }))}
            />
          </Form.Item>

          {editingUserId && (
            <>
              <Button
                type="dashed"
                block
                style={{ marginBottom: 16 }}
                onClick={() => {
                  setShowPasswordFields(v => !v);
                  form.resetFields(['newPassword', 'confirmPassword']);
                }}
              >
                {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
              </Button>

              {showPasswordFields && (
                <>
                  <Form.Item
                    label="New Password"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'Please enter new password' },
                      { min: 6, message: 'Password must be at least 6 characters' }
                    ]}
                  >
                    <Input.Password placeholder="Enter new password" />
                  </Form.Item>

                  <Form.Item
                    label="Confirm Password"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: 'Please confirm password' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords do not match'));
                        }
                      })
                    ]}
                  >
                    <Input.Password placeholder="Confirm new password" />
                  </Form.Item>
                </>
              )}
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
